import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, colors, Table, Typography } from '@leapeasy/ui-kit';
import { TableEmptyState } from 'components/common/TableEmptyState';
import { Box } from '@mui/material';
import styled from 'styled-components';
import { getBuildingDelegationAction } from 'store/actions/propertyActions';
import { USER_ROLE, getDemoData } from 'data/constants/common_constants';
import { useListQueryState } from 'hooks/useListQueryState';
import {
  applyClientListState,
  BUILDING_DETAILS_URL_PARAM_KEYS,
  DEFAULT_DETAILS_TABLE_FILTER,
  DETAILS_DELEGATION_QUERY_PARAMS,
  DETAILS_SUMMARY_QUERY_PARAMS,
} from 'utils/buildingDetailsListQueryParams';

export const DETAILS_TAB_VARIANT = {
  /** Email / user-by-role / landlord / invite flows — summary cards only */
  EMBEDDED: 'embedded',
  /** Property portal building detail — responsive grids + delegation table */
  PROPERTY_PORTAL: 'property_portal',
};

const RateCard = ({ title, value }) => (
  <Box
    display="flex"
    gap="8px"
    sx={{ background: colors.blue[100], borderRadius: '8px', padding: '12px' }}
    alignItems="center"
    flexGrow={1}
  >
    <Typography variant="body2" style={{ color: colors.blue[600], fontWeight: '600' }}>
      {value}
    </Typography>

    <Box sx={{ border: `1px solid ${colors.blue[300]}`, flexGrow: 1, height: '0px' }} />

    <Typography variant="body3" style={{ color: colors.neutral[900], fontWeight: '500' }}>
      {title}
    </Typography>
  </Box>
);

const SummaryCard = ({ title, value }) => (
  <Box
    sx={{
      background: '#F9F8F9',
      border: `1px solid ${colors.black[200]}`,
    }}
    boxSizing="border-box"
    width="25%"
    flexShrink={1}
    padding="12px"
    gap="8px"
    borderRadius="8px"
    display="flex"
    justifyContent="space-between"
  >
    <Typography variant="body2" style={{ fontWeight: '600', color: colors.purple[900] }}>
      {value}
    </Typography>

    <Typography variant="body3" style={{ fontWeight: '500', color: '#A39AA9' }}>
      {title}
    </Typography>
  </Box>
);

const EmbeddedBuildingDetails = ({ building }) => (
  <Box>
    <Box display="flex" flexDirection="column" sx={{ padding: '0px 24px', gap: '4px' }}>
      <Box display="flex" flexDirection="column" padding="16px 0px" gap="12px">
        <Typography variant="body1" style={{ color: colors.purple[900], fontWeight: '600' }}>
          Property Level Occupancy Rates
        </Typography>

        <Box display="flex" gap="8px">
          <RateCard title="Total # of Units" value={building.total_of_units} />
          <RateCard title="Leap % Total Inventory" value={building.leap_of_total_inventory} />
          <RateCard
            title="Estimated App. Decline Rate"
            value={building.estimated_applicant_decline_rate}
          />
        </Box>
      </Box>

      <Box sx={{ border: `1px solid ${colors.black[200]}` }} />

      <Box display="flex" flexDirection="column" padding="16px 0px" gap="12px">
        <Typography variant="body1" style={{ color: colors.purple[900], fontWeight: '600' }}>
          Property Summary Data
        </Typography>

        <Box display="flex" gap="8px" flexDirection="column">
          <Box display="flex" gap="8px">
            <SummaryCard title="Last App. Date" value={building.last_application_date} />
            <SummaryCard title="Total # of Applications" value={building.total_of_applications} />
            <SummaryCard
              title="Total # of Decisioned App"
              value={building.total_of_decisioned_app}
            />
            <SummaryCard title="Property Approval Rate" value={building.building_approval_rate} />
          </Box>

          <Box display="flex" gap="8px">
            <SummaryCard
              title="Total # of Cancelled Apps."
              value={building.total_of_cancelled_applications}
            />
            <SummaryCard
              title="Property Approved to Issue"
              value={building.building_approved_to_issue_rate}
            />
            <SummaryCard title="Total # of Approved Apps" value={building.total_of_approved_app} />
            <SummaryCard
              title="Total # of Issued Policies"
              value={building.total_of_issued_policies}
            />
          </Box>

          <Box display="flex" gap="8px">
            <SummaryCard
              title="Total # of Declined App"
              value={building.total_of_declined_applications}
            />
            <SummaryCard title="Cancellation Rate" value={building.cancellation_percentage} />
            <SummaryCard title="Declined Rate" value={building.decline_percentage} />
            <Box flexShrink={1} width="25%" />
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

const CustomTypography = styled(Typography)`
  word-break: break-all;
  overflow-wrap: anywhere;
`;

const RateCardContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  @media screen and (max-width: ${(props) => props.theme.lg}) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media screen and (max-width: ${(props) => props.theme.sm}) {
    grid-template-columns: repeat(1, 1fr);
  }
`;

const propertySummaryColumns = [
  {
    name: 'Metric',
    key: 'label',
    options: {
      flex: 'minmax(200px, 1fr) 1 1',
      sort: true,
      customBodyRenderer: (value) => (
        <Typography variant="body3" style={{ color: '#A39AA9', fontWeight: '500' }}>
          {value}
        </Typography>
      ),
    },
  },
  {
    name: 'Value',
    key: 'value',
    options: {
      flex: 'minmax(120px, 1fr) 1 1',
      sort: true,
      customBodyRenderer: (value) => (
        <Typography variant="body2" style={{ fontWeight: '600', color: colors.purple[900] }}>
          {value != null && value !== '' ? String(value) : '—'}
        </Typography>
      ),
    },
  },
];

const PropertyPortalBuildingDetails = ({ building }) => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const previousBuildingIdRef = useRef(undefined);
  const summarySearchResetRef = useRef(false);
  const delegationSearchResetRef = useRef(false);

  const delegation = useSelector((state) => state.getIn(['property', 'delegation']));
  const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

  const [tableData, setTableData] = useState([]);

  const {
    filter: summaryFilter,
    setFilter: setSummaryFilter,
    sortOptions: summarySortOptions,
    setSortOptions: setSummarySortOptions,
    pagination: summaryPagination,
    setPagination: setSummaryPagination,
  } = useListQueryState({
    defaultFilter: DEFAULT_DETAILS_TABLE_FILTER,
    params: DETAILS_SUMMARY_QUERY_PARAMS,
    defaultRowsPerPage: 12,
  });

  const {
    filter: delegationFilter,
    setFilter: setDelegationFilter,
    sortOptions: delegationSortOptions,
    setSortOptions: setDelegationSortOptions,
    pagination: delegationPagination,
    setPagination: setDelegationPagination,
  } = useListQueryState({
    defaultFilter: DEFAULT_DETAILS_TABLE_FILTER,
    params: DETAILS_DELEGATION_QUERY_PARAMS,
    defaultRowsPerPage: 12,
  });

  useLayoutEffect(() => {
    const id = building?.building_id;
    if (!id) return;
    if (previousBuildingIdRef.current !== id) {
      if (previousBuildingIdRef.current !== undefined) {
        // Don't wipe ds*/dd* params when user is refreshing with active filters.
        // These params are already encoded in the URL and should restore UI state.
        const hasAnyDetailsTableParam = BUILDING_DETAILS_URL_PARAM_KEYS.some((key) =>
          searchParams.has(key),
        );

        if (!hasAnyDetailsTableParam) {
          const next = new URLSearchParams(searchParams);
          BUILDING_DETAILS_URL_PARAM_KEYS.forEach((key) => next.delete(key));
          setSearchParams(next, { replace: true });
        }
      }
      previousBuildingIdRef.current = id;
    }
  }, [building?.building_id, searchParams, setSearchParams]);

  useEffect(() => {
    if (!summarySearchResetRef.current) {
      summarySearchResetRef.current = true;
      return;
    }
    setSummaryPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [summaryFilter.searchText, setSummaryPagination]);

  useEffect(() => {
    if (!delegationSearchResetRef.current) {
      delegationSearchResetRef.current = true;
      return;
    }
    setDelegationPagination((prev) => (prev.pageNumber === 0 ? prev : { ...prev, pageNumber: 0 }));
  }, [delegationFilter.searchText, setDelegationPagination]);

  useEffect(() => {
    if (!building?.building_id) return;
    dispatch(getBuildingDelegationAction(building.building_id));
  }, [dispatch, building?.building_id]);

  useEffect(() => {
    const delegationJSON = delegation ? delegation.toJS() : null;

    const rawRows = Array.isArray(delegationJSON)
      ? delegationJSON
      : Array.isArray(delegationJSON?.data)
        ? delegationJSON.data
        : Array.isArray(delegationJSON?.delegations)
          ? delegationJSON.delegations
          : [];

    const toPlain = (row) => {
      if (row == null) return null;
      return typeof row.toJS === 'function' ? row.toJS() : row;
    };

    const delegatedUser = rawRows
      .map(toPlain)
      .filter((user) => user && typeof user === 'object' && !Array.isArray(user))
      .map((user) => ({
        accepter_role: user.accepter_role ?? '',
        job_title: user.accepter?.job_title || 'No Data',
        accepter_email: isDemo ? getDemoData('email') : user.accepter_email || 'No Data',
        requestor_role: user.requestor_role ?? '',
        requestor_email: isDemo ? getDemoData('email') : user.requestor_email || 'No Data',
        createdAt: user.createdAt || user.created_at || '',
        updatedAt: user.updatedAt || user.updated_at || '',
      }));

    setTableData(delegatedUser);
  }, [delegation, isDemo]);

  const propertySummaryRows = useMemo(
    () => [
      { label: 'Last App. Date', value: building?.last_application_date },
      { label: 'Total # of Applications', value: building?.total_of_applications },
      { label: 'Total # of Decisioned App', value: building?.total_of_decisioned_app },
      { label: 'Property Approval Rate', value: building?.building_approval_rate },
      { label: 'Total # of Cancelled Apps.', value: building?.total_of_cancelled_applications },
      { label: 'Property Approved to Issue', value: building?.building_approved_to_issue_rate },
      { label: 'Total # of Approved Apps', value: building?.total_of_approved_app },
      { label: 'Total # of Issued Policies', value: building?.total_of_issued_policies },
      { label: 'Total # of Declined App', value: building?.total_of_declined_applications },
      { label: 'Cancellation Rate', value: building?.cancellation_percentage },
      { label: 'Declined Rate', value: building?.decline_percentage },
    ],
    [building],
  );

  const summaryListState = useMemo(
    () =>
      applyClientListState({
        rows: propertySummaryRows,
        filter: summaryFilter,
        sortOptions: summarySortOptions,
        pagination: summaryPagination,
        searchKeys: ['label', 'value'],
      }),
    [propertySummaryRows, summaryFilter, summarySortOptions, summaryPagination],
  );

  const delegationListState = useMemo(
    () =>
      applyClientListState({
        rows: tableData,
        filter: delegationFilter,
        sortOptions: delegationSortOptions,
        pagination: delegationPagination,
        searchKeys: [
          'accepter_role',
          'job_title',
          'accepter_email',
          'requestor_role',
          'requestor_email',
          'createdAt',
          'updatedAt',
        ],
      }),
    [tableData, delegationFilter, delegationSortOptions, delegationPagination],
  );

  useEffect(() => {
    if (summaryListState.pageNumber === summaryPagination.pageNumber) return;
    setSummaryPagination((prev) => ({ ...prev, pageNumber: summaryListState.pageNumber }));
  }, [summaryListState.pageNumber, summaryPagination.pageNumber, setSummaryPagination]);

  useEffect(() => {
    // When delegation rows are not loaded yet, applyClientListState clamps `pageNumber`
    // to 0 (because totalItems=0). That prematurely rewrites ddPage to 1 in the URL.
    // Only sync/clamp once we have rows.
    if (delegationListState.totalItems === 0) return;
    if (delegationListState.pageNumber === delegationPagination.pageNumber) return;
    setDelegationPagination((prev) => ({ ...prev, pageNumber: delegationListState.pageNumber }));
  }, [delegationListState.pageNumber, delegationPagination.pageNumber, setDelegationPagination]);

  const delegationColumns = [
    {
      name: 'Role',
      options: {
        flex: '120px 1 1',
        customBodyRenderer: (value) => (
          <Badge
            background="#F3F1F4"
            color={value === 'invite' ? 'purple' : 'tomato'}
            label={(USER_ROLE[value] ?? value) || ''}
            rounded
            textSize="medium"
          />
        ),
      },
      key: 'accepter_role',
    },
    {
      name: 'Job Title',
      options: {
        sort: true,
        customBodyRenderer: (value) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'job_title',
    },
    {
      name: 'Email',
      options: {
        sort: true,
        customBodyRenderer: (value) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'accepter_email',
    },
    {
      name: 'Assigner Role',
      options: {
        customBodyRenderer: (value) => (
          <Badge
            background="#1C1598"
            color="purpleIrish"
            label={(USER_ROLE[value] ?? value) || ''}
            rounded
            textSize="medium"
          />
        ),
        flex: '225px 1 1',
      },
      key: 'requestor_role',
    },
    {
      name: 'Assigner Email',
      options: {
        customBodyRenderer: (value) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value}
          </CustomTypography>
        ),
      },
      key: 'requestor_email',
    },
    {
      name: 'Created Date',
      options: {
        customBodyRenderer: (value) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {typeof value === 'string' && value.includes('T')
              ? value.split('T')[0]
              : value || 'No Data'}
          </CustomTypography>
        ),
      },
      key: 'createdAt',
    },
    {
      name: 'Last Updated Date',
      options: {
        flex: '50px 1 1',
        customBodyRenderer: (value) => (
          <CustomTypography align="left" variant="body2" style={{ cursor: 'pointer' }}>
            {value || 'No Data'}
          </CustomTypography>
        ),
      },
      key: 'updatedAt',
    },
  ];

  return (
    <Box>
      <Box display="flex" flexDirection="column" sx={{ padding: '0px 24px', gap: '4px' }}>
        <Box display="flex" flexDirection="column" padding="16px 0px" gap="12px">
          <Typography variant="body1" style={{ color: colors.purple[900], fontWeight: '600' }}>
            Property Level Occupancy Rates
          </Typography>

          <RateCardContainer display="flex" gap="8px">
            <RateCard title="Total # of Units" value={building.total_of_units} />
            <RateCard title="Leap % Total Inventory" value={building.leap_of_total_inventory} />
            <RateCard
              title="Estimated App. Decline Rate"
              value={building.estimated_applicant_decline_rate}
            />
          </RateCardContainer>
        </Box>

        <Box sx={{ border: `1px solid ${colors.black[200]}` }} />

        <Box display="flex" flexDirection="column" padding="16px 0px" gap="12px">
          <Table
            title="Property Summary Data"
            columnWidthStorageKey="building-details-summary"
            data={summaryListState.pageRows}
            columns={propertySummaryColumns}
            filter={summaryFilter}
            onChangeFilter={setSummaryFilter}
            sortOptions={summarySortOptions}
            onChangeSort={setSummarySortOptions}
            showEmptyState={true}
            emptyContent={
              <TableEmptyState
                entityName="property summary records"
                hasActiveFilters={Boolean(`${summaryFilter?.searchText || ''}`.trim())}
              />
            }
            onChangeRowsPerPage={(value) =>
              setSummaryPagination((prev) => ({
                ...prev,
                rowsPerPage: Number(value),
                pageNumber: 0,
              }))
            }
            pagination={{
              ...summaryPagination,
              pageNumber: summaryListState.pageNumber,
              totalItems: summaryListState.totalItems,
              totalPages: summaryListState.totalPages,
            }}
            style={{ width: '100%' }}
          />
        </Box>

        <Box display="flex" flexDirection="column" padding="16px 0px" gap="12px">
          <Table
            title="Property Summary Data"
            columnWidthStorageKey="building-details-delegation"
            data={delegationListState.pageRows}
            columns={delegationColumns}
            filter={delegationFilter}
            onChangeFilter={setDelegationFilter}
            sortOptions={delegationSortOptions}
            onChangeSort={setDelegationSortOptions}
            showEmptyState={true}
            emptyContent={
              <TableEmptyState
                entityName="delegation records"
                hasActiveFilters={Boolean(`${delegationFilter?.searchText || ''}`.trim())}
              />
            }
            onChangeRowsPerPage={(value) =>
              setDelegationPagination((prev) => ({
                ...prev,
                rowsPerPage: Number(value),
                pageNumber: 0,
              }))
            }
            pagination={{
              ...delegationPagination,
              pageNumber: delegationListState.pageNumber,
              totalItems: delegationListState.totalItems,
              totalPages: delegationListState.totalPages,
            }}
            style={{ width: '100%' }}
          />
        </Box>
      </Box>
    </Box>
  );
};

export const DetailsTab = ({ building, variant = DETAILS_TAB_VARIANT.EMBEDDED }) => {
  if (variant === DETAILS_TAB_VARIANT.PROPERTY_PORTAL) {
    return <PropertyPortalBuildingDetails building={building} />;
  }
  return <EmbeddedBuildingDetails building={building} />;
};
